interface CustoFuncionarioInputData {
  readonly salarioBruto: string;
  readonly valeTransporte: string;
  readonly valeAlimentacao: string;
  readonly planoSaude: string;
  readonly outrosBeneficios: string;
  readonly regimeEmpresa: 'simples' | 'lucro';
}

interface DescontosFuncionarioType {
  readonly inss: number;
  readonly irrf: number;
  readonly valeTransporte: number;
  readonly totalDescontos: number;
}

interface EncargosPagtosType {
  readonly inssPatronal: number;
  readonly fgts: number;
  readonly salarioEducacao: number;
  readonly sistemaS: number;
  readonly seguroAcidente: number;
  readonly totalEncargos: number;
}

interface BeneficiosPagtosType {
  readonly valeTransporteEmpresa: number;
  readonly valeAlimentacao: number;
  readonly planoSaude: number;
  readonly outrosBeneficios: number;
  readonly totalBeneficios: number;
}

interface CustoMensalType {
  readonly salarioBruto: number;
  readonly salarioLiquido: number;
  readonly descontosFuncionario: DescontosFuncionarioType;
  readonly encargosPagos: EncargosPagtosType;
  readonly beneficiosPagos: BeneficiosPagtosType;
  readonly custoTotalMensal: number;
}

interface ProvisaoAnualType {
  readonly decimoTerceiro: number;
  readonly decimoTerceiroEncargos: number;
  readonly feriasLiquidas: number;
  readonly feriasEncargos: number;
  readonly fgtsAnual: number;
  readonly provisaoMultaFGTS: number;
  readonly totalProvisoes: number;
}

interface CustoAnualType {
  readonly custoMensal12x: number;
  readonly provisoes: ProvisaoAnualType;
  readonly beneficiosAnuais: number;
  readonly custoTotalAnual: number;
}

interface ComparativoRegimesType {
  readonly regimeAtual: string;
  readonly regimeComparacao: string;
  readonly diferenciaMensal: number;
  readonly diferencaAnual: number;
  readonly diferencaPercentual: number;
  readonly custoAtual: number;
  readonly custoComparacao: number;
}

interface CalculoBreakdownFuncionario {
  readonly salarioBruto: number;
  readonly inssBreakdown: {
    readonly faixas: Array<{
      readonly limite: number;
      readonly aliquota: number;
      readonly valorFaixa: number;
      readonly contribuicaoFaixa: number;
    }>;
    readonly valorTotal: number;
  };
  readonly irrfBreakdown: {
    readonly baseCalculo: number;
    readonly faixaAplicada: {
      readonly aliquota: number;
      readonly parcelaDeducao: number;
    };
    readonly valorIRRF: number;
  };
  readonly valeTransporteBreakdown: {
    readonly valorTotal: number;
    readonly descontoFuncionario: number;
    readonly custoEmpresa: number;
    readonly limiteSalarial: number;
  };
}

interface CustoFuncionarioResultsType {
  readonly custoMensal: CustoMensalType;
  readonly custoAnual: CustoAnualType;
  readonly comparativo: ComparativoRegimesType;
  readonly breakdown: CalculoBreakdownFuncionario;
  readonly regime: 'simples' | 'lucro';
}

// Mensagens de validação
const ValidationMessages = {
  SALARIO_OBRIGATORIO: "Informe o salário bruto.",
  SALARIO_INVALIDO: "Salário bruto deve ser maior que zero.",
  SALARIO_MINIMO: "Salário deve ser pelo menos R$ 1.518,00 (salário mínimo).",
  VALE_TRANSPORTE_INVALIDO: "Vale transporte deve ser maior que zero.",
  ERRO_CALCULO: "Erro no cálculo. Verifique os dados informados.",
} as const;

const CONSTANTES_2025 = {
  SALARIO_MINIMO: 1518.00,
  TETO_INSS: 8157.41,
  VALOR_DEPENDENTE_IRRF: 189.59,
  FGTS_ALIQUOTA: 0.08,
  LIMITE_ISENCAO_IRRF: 3036.00, // ADICIONADO: 2 salários mínimos (R$ 1.518 × 2)
} as const;

const REGIMES_EMPRESA_2025 = {
  simples: {
    nome: 'Simples Nacional',
    descricao: 'Empresas optantes pelo Simples Nacional - Isenção de encargos patronais',
    inssPatronal: 0,        
    salarioEducacao: 0,     
    sistemaS: 0,           
    seguroAcidente: 0,     
    fgts: 0.08,          
    totalEncargos: 0.08,
    economia: 'Economia de 27,8% em encargos vs Lucro Presumido/Real'
  },
  lucro: {
    nome: 'Lucro Presumido/Real',
    descricao: 'Tributação completa com todos os encargos trabalhistas',
    inssPatronal: 0.20,     
    salarioEducacao: 0.025, 
    sistemaS: 0.033,       
    seguroAcidente: 0.02,  
    fgts: 0.08,           
    totalEncargos: 0.358, 
    detalhamento: 'INSS 20% + Sal.Educação 2,5% + Sistema S 3,3% + Seguro 2% + FGTS 8%'
  }
} as const;

const EXPLICACOES_CAMPOS = {
  salarioBruto: "Valor acordado em contrato antes dos descontos. Base para cálculo de todos os encargos.",
  
  regimeEmpresa: "O regime tributário da empresa impacta diretamente nos encargos sobre a folha de pagamento.",
  
  simplesNacional: "Empresas do Simples Nacional mantêm INSS Patronal (20%) e FGTS (8%), mas têm ISENÇÃO de: Salário Educação (2,5%), Sistema S (3,3%) e Seguro Acidente (2%). Total: 28% vs 35,8% do Lucro Presumido.",
  
  lucroPresumido: "Empresas do Lucro Presumido/Real pagam todos os encargos: INSS Patronal 20% + Salário Educação 2,5% + Sistema S 3,3% + Seguro Acidente 2% + FGTS 8% = 35,8% total.",
  
  valeTransporte: "Funcionário desconta máximo 6% do salário. Empresa arca com valor total. A diferença é custo adicional da empresa.",
  
  provisaoFGTS: "Reserva de 40% sobre saldo FGTS para cobrir multa em caso de demissão sem justa causa.",
  
  provisaoSalarios: "Reserve de 3 a 6 meses de custos para cobrir transições, férias coletivas ou processos de demissão.",

  irrfIsencao: "IRRF isento para quem ganha até R$ 3.036,00 (dois salários mínimos de 2025). Lei nº 15.191/2025."
} as const;

export type {
  CustoFuncionarioResultsType,
  CustoFuncionarioInputData,
  CustoMensalType,
  CustoAnualType,
  ComparativoRegimesType,
  DescontosFuncionarioType,
  EncargosPagtosType,
  BeneficiosPagtosType,
  ProvisaoAnualType,
  CalculoBreakdownFuncionario,
};

export { ValidationMessages, CONSTANTES_2025, REGIMES_EMPRESA_2025, EXPLICACOES_CAMPOS };